import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { FileText, Search } from 'lucide-react';
import { appId, db } from '@/lib/firebase';

interface AdminStripeInvoicesProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface StripeInvoiceRecord {
  id: string;
  userId: string | null;
  stripeInvoiceId: string;
  amount: number;
  currency: string;
  status: string;
  dueDate?: { toDate: () => Date } | null;
  paidAt?: { toDate: () => Date } | null;
  hostedInvoiceUrl?: string | null;
  invoicePdf?: string | null;
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDateTime(ts?: { toDate: () => Date } | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function AdminStripeInvoices({ showNotify: _showNotify }: AdminStripeInvoicesProps): JSX.Element {
  void _showNotify;
  const [invoices, setInvoices] = useState<StripeInvoiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'>(
    'all'
  );

  useEffect(() => {
    const q = query(collection(db, 'artifacts', appId, 'invoices'), orderBy('updatedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const mapped: StripeInvoiceRecord[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as Partial<StripeInvoiceRecord>;
          return {
            id: docSnap.id,
            userId: data.userId ?? null,
            stripeInvoiceId: data.stripeInvoiceId ?? docSnap.id,
            amount: data.amount ?? 0,
            currency: data.currency ?? 'usd',
            status: data.status ?? 'draft',
            dueDate: data.dueDate ?? null,
            paidAt: data.paidAt ?? null,
            hostedInvoiceUrl: data.hostedInvoiceUrl ?? null,
            invoicePdf: data.invoicePdf ?? null,
          };
        });
        setInvoices(mapped);
        setLoading(false);
      },
      () => {
        setInvoices([]);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const filtered = useMemo(
    () =>
      invoices.filter((invoice) => {
        if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;
        if (!search.trim()) return true;
        const needle = search.toLowerCase();
        return (
          invoice.stripeInvoiceId.toLowerCase().includes(needle) ||
          (invoice.userId ?? '').toLowerCase().includes(needle)
        );
      }),
    [invoices, search, statusFilter]
  );

  return (
    <section className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText size={20} className="text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900">Stripe Invoices</h1>
        </div>
        <p className="text-sm text-gray-600">
          Monitor invoice statuses and open hosted invoice or PDF links for customer billing operations.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by invoice ID or user ID"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </label>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="paid">Paid</option>
          <option value="uncollectible">Uncollectible</option>
          <option value="void">Void</option>
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-8 text-sm text-gray-600 text-center">Loading invoices...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-sm text-gray-600 text-center">No invoices found for current filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Invoice</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">User</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Amount</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Due Date</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Paid At</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Links</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((invoice) => (
                <tr key={invoice.id} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-3 text-gray-900 font-medium">{invoice.stripeInvoiceId}</td>
                  <td className="px-4 py-3 text-gray-700">{invoice.userId ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{formatMoney(invoice.amount, invoice.currency)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : invoice.status === 'open'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatDateTime(invoice.dueDate)}</td>
                  <td className="px-4 py-3 text-gray-700">{formatDateTime(invoice.paidAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {invoice.hostedInvoiceUrl ? (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                        >
                          Hosted
                        </a>
                      ) : null}
                      {invoice.invoicePdf ? (
                        <a
                          href={invoice.invoicePdf}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                        >
                          PDF
                        </a>
                      ) : null}
                      {!invoice.hostedInvoiceUrl && !invoice.invoicePdf ? (
                        <span className="text-xs text-gray-500">No links</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
